export const noop = () => {
  // No operation function
};

/**
 * Creates a debounced version of a function that delays its execution until after a specified wait time
 * has elapsed since the last time it was invoked.
 *
 * @template T - The type of the arguments array
 * @template U - The type of the callback function
 * @param callback - The function to debounce
 * @param debounceMilliseconds - The number of milliseconds to delay (default: 200)
 * @returns A debounced version of the callback function
 */
export type DebouncedFn<T extends unknown[]> = ((...args: T) => void) & {
  cancel: () => void;
};

export const debounce = <
  T extends unknown[],
  U extends (...args: T) => unknown,
>(
  callback: U,
  debounceMilliseconds = 200,
): DebouncedFn<T> => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: T): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      callback(...args);
    }, debounceMilliseconds);
  };

  debounced.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
};
